import React, { useState, useMemo, useEffect, useCallback } from "react"
import { useMutation, useQuery } from "react-query"
import { useHistory } from "react-router";
import { Button } from "../components/button";
import { fetchProjects, fetchUser, fetchPostOfferProjects, getProjectContractPDF, archiveProject, unarchiveProject } from "../services"
import { Grid, Container, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Menu, MenuItem } from "@material-ui/core"
import TableContainerServerSide from "../components/TableContainerServerSide"
import SelectColumnFilter from "../components/SelectFilter"
import DefaultFilter from "../components/DefaultFilter"
import slugify from 'slugify';
import moment from "moment";
import { RatingModal } from "../components/RatingModal";
import { useAuth } from "../utils/Auth";
import { isStaff } from "../utils";
import Tooltip from '@mui/material/Tooltip';
import Snackbar from '@mui/material/Snackbar';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import MoreVertIcon from '@material-ui/icons/MoreVert';

function ProjectRowActions({
  row,
  user,
  downloadContract,
  downloading,
  archive,
  unarchive,
  isArchiving,
  isUnarchiving,
  setReviewData,
  onUrlCopied,
}) {
  const [anchorEl, setAnchorEl] = useState(null)
  const open = Boolean(anchorEl)
  const project = row.original
  const {
    id,
    uid,
    status_id,
    current_month,
    experts = [],
    has_videos,
    is_archived,
    name,
    company_name,
  } = project

  const closeMenu = () => setAnchorEl(null)
  const handleOpen = (event) => {
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
  }

  const isFinanceAdmin = user?.email == process.env.REACT_APP_FINANCE_ADMIN_EMAIL
  const canSeePulses = isStaff(user) || user.email === process.env.REACT_APP_AMIRA_EMAIL
  const canRateCreator = experts.some(e => current_month > e.is_reviewed)
  const canDownloadContract = status_id == process.env.REACT_APP_PROJECT_STATUS_ONGOING

  const copyProjectUrl = async () => {
    closeMenu()
    const message = `Hello, I'm interested in this project.\n\nprojectVideoUid = ${id}`
    const url = `https://wa.me/966553771671?text=${encodeURIComponent(message)}`
    try {
      await navigator.clipboard.writeText(url)
      onUrlCopied()
    } catch (e) {
      alert('Could not copy the link')
    }
  }

  return (
    <div onClick={(event) => event.stopPropagation()}>
      <IconButton
        size="small"
        aria-label="More actions"
        aria-controls={open ? `project-actions-menu-${id}` : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleOpen}
      >
        <MoreVertIcon />
      </IconButton>
      <Menu
        id={`project-actions-menu-${id}`}
        anchorEl={anchorEl}
        open={open}
        onClose={closeMenu}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {isFinanceAdmin ? (
          [
            <MenuItem key="payments" component="a" href={`/project-payments/${id}`} onClick={closeMenu}>
              Payments
            </MenuItem>,
            <MenuItem key="url" onClick={copyProjectUrl}>
              Url
            </MenuItem>,
          ]
        ) : (
          [
            canRateCreator && (
              <MenuItem
                key="rate"
                onClick={() => {
                  closeMenu()
                  setReviewData({
                    projectId: id,
                    month: current_month,
                    experts: experts.filter(e => current_month > e.is_reviewed).map(e => ({
                      name: e.expert_name,
                      id: e.expert_id,
                      avatar: e.expert_avatar,
                      months: e.months_reviewed,
                    })),
                  })
                }}
              >
                Rate Creator
              </MenuItem>
            ),
            <MenuItem
              key="edit"
              component="a"
              href={has_videos ? `/edit-project/${id}/ugc` : `/edit-project/${id}`}
              onClick={closeMenu}
            >
              Edit
            </MenuItem>,
            <MenuItem key="brief" component="a" href={`/project-brief/${id}`} onClick={closeMenu}>
              Brief
            </MenuItem>,
            <MenuItem
              key="chat"
              component="a"
              href={`/chats/${slugify(name || company_name)}/${id}`}
              onClick={closeMenu}
            >
              Chat
            </MenuItem>,
            <MenuItem
              key="view"
              component="a"
              href={`${process.env.REACT_APP_STLLR_URL}campaign/${uid}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={closeMenu}
            >
              View
            </MenuItem>,
            <MenuItem key="url" onClick={copyProjectUrl}>
              Url
            </MenuItem>,
            canSeePulses && (
              <MenuItem key="pulses" component="a" href={`/pulses/${id}`} onClick={closeMenu}>
                Pulses
              </MenuItem>
            ),
            canDownloadContract && (
              <MenuItem
                key="contract"
                disabled={downloading === id}
                onClick={() => {
                  closeMenu()
                  downloadContract(project)
                }}
              >
                Download Contract
              </MenuItem>
            ),
            <MenuItem
              key="archive"
              disabled={is_archived ? isUnarchiving : isArchiving}
              onClick={() => {
                closeMenu()
                is_archived ? unarchive(id) : archive(id)
              }}
            >
              {is_archived ? 'Unarchive' : 'Archive'}
            </MenuItem>,
          ]
        )}
      </Menu>
    </div>
  )
}

function Projects() {
  const history = useHistory()
  const { user } = useAuth()
  const [reviewData, setReviewData] = useState(null)
  const [total, setTotal] = useState(0)
  const [pageCount, setPageCount] = useState(1)
  const [projects, setProjects] = useState([])
  const [downloading, setDownloading] = useState(null)
  const [urlCopied, setUrlCopied] = useState(false)

  // const { data: user, isLoading: userLoading } = useQuery("user", fetchUser, {
  //   select: (data) => data.data,
  //   onSuccess: () => {
  //   }
  // })

  const downloadContract = async (project) => {
    try {
      const { id, company_name, company_role, owner_name, owner_email, price_formatted, duration } = project
      setDownloading(id)

      const body = {
        id: project.id,
        // company_name: project.company_name,
        // company_role: project.company_role,
        // owner_name: project.owner_name,
        // owner_email: project.owner_email,
        // price_formatted: project.price_formatted,
        // duration: project.duration,
      }

      const data = await getProjectContractPDF(body)

      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(new Blob([data]))
      link.setAttribute('download', `${company_name}_${company_role}_${moment().format('L')}.pdf`);
      link.click()

      setDownloading(null)
    }
    catch (e) {
      //console.log(e)
      alert('Oops, something went wrong!')
      setDownloading(null)
    }
  }


  const columns = useMemo(() => [
    {
      accessor: 'company_name',
      Header: 'Company',
      Filter: DefaultFilter
    },
    {
      accessor: 'name',
      Header: 'Project name',
      Filter: DefaultFilter,
      Cell: ({ row }) => {
        const { name, is_offer, videos_fulfilled } = row.original
        return (
          <div className="is-flex aic">
            {name}
            {!is_offer && !videos_fulfilled && (
              <span className="ml-10">
                <Tooltip title="Video(s) in there need to be fulfilled" arrow>
                  <WarningAmberRoundedIcon style={{ color: '#f1b951' }} />
                </Tooltip>
              </span>
            )}
          </div>)
      }
    },
    {
      accessor: 'project_status',
      Header: 'Status',
      Cell: ({ row }) => {
        const { status_id, project_status } = row.original
        let statusClass = ``

        switch (Number(status_id)) {
          case Number(process.env.REACT_APP_PROJECT_STATUS_AWAITING_PAYMENT):
            statusClass = `-red`
            break;

          case Number(process.env.REACT_APP_PROJECT_STATUS_PENDING_BRIEF):
            statusClass = `-orange`
            break;

          case Number(process.env.REACT_APP_PROJECT_STATUS_PENDING_MILESTONES):
            statusClass = `-blue`
            break;

          case Number(process.env.REACT_APP_PROJECT_STATUS_ONGOING):
            statusClass = `-gold`
            break;

          case Number(process.env.REACT_APP_PROJECT_STATUS_PAUSED_BY_CUSTOMER):
            statusClass = `-purple`
            break;

          case Number(process.env.REACT_APP_PROJECT_STATUS_PAUSED):
            statusClass = `-yellow`
            break;

          case Number(process.env.REACT_APP_PROJECT_STATUS_CANCELLED):
            statusClass = `-grey`
            break;

          default:
            statusClass = `-green`
        }

        return <Chip size="small" className={`chip${statusClass}`} label={project_status} />
      },
      Filter: SelectColumnFilter,
      FilterOptions: [
        'Awaiting payment',
        'Pending Brief',
        'Pending milestones',
        'Ongoing',
        'Paused by Customer',
        'Paused',
        'Cancelled',
        'Finished',
        'Payment Failed',
        'Expert Replaced',
        'Pending Offer',
        'Offer Rejected',
        'Offer Submitted',
      ]
    },
    {
      Header: 'Catalog only',
      id: 'is_catalog',
      accessor: `is_catalog`,
      Filter: SelectColumnFilter,
      FilterOptions: ['Yes', 'No'],
      showColumn: false,
  },
    {
      accessor: `video_count`,
      Header: `#Videos`,
      disableFilters: true,
      Cell: ({ row }) => {
        const { video_count } = row.original
        return <span>{video_count ? `${video_count}` : ``}</span>
      }
    },
    {
      Header: 'Revenue',
      accessor: 'price',
      disableFilters: true,
      Cell: ({ row }) => {
        const { price_formatted } = row.original
        return <span>{price_formatted}</span>
      }
    },
    {
      accessor: 'cost',
      Header: 'Cost',
      disableFilters: true,
      Cell: ({ row }) => {
        const { payout_formatted } = row.original
        return <span>{payout_formatted}</span>
      }
    },
    // {
    //   accessor: 'payout',
    //   Header: 'Payouts',
    //   disableFilters: true,
    //   Cell: ({ row }) => {
    //     const { payout_formatted } = row.original
    //     return <span>{payout_formatted}</span>
    //   }
    // },
    {
      Header: 'Gross income',
      accessor: 'gross_income',
      disableFilters: true,
      Cell: ({ row }) => {
        const { price, payout, currency_symbol } = row.original
        return <span>{currency_symbol}{(price - payout).toFixed(2)}</span>
      }
    },
    {
      accessor: 'n_experts',
      Header: '#Creators',
      disableFilters: true,
      Cell: ({ row }) => {
        const { experts } = row.original
        return <span>{experts.length}</span>
      }
    },
    {
      accessor: 'type',
      Header: 'Type',
      disableFilters: true,
      Cell: ({ row }) => {
        const { creator_role_id, name, is_offer } = row.original
        return (
          <div className="is-flex aic">
            <span>{creator_role_id == process.env.REACT_APP_ADMIN_ROLE_ID ? `Team` : `Self-Checkout`}</span>
            {is_offer === 1 && <Chip size="small" className="chip-blue" label="Catalog" style={{ marginLeft: '8px' }} />}
          </div>
        )
      }
    },
    {
      accessor: 'actual_commission',
      Header: 'Comm.%',
      disableFilters: true,
      Cell: ({ row }) => {
        const { actual_commission } = row.original
        return <span>{actual_commission}</span>
      }
    },
    {
      Header: 'Created date',
      accessor: "date_add",
      Filter: DefaultFilter,
      FilterPlaceholder: 'YYYY-MM-DD',
      Cell: ({ row }) => {
        const { date_add } = row.original
        return moment(date_add).format('ll')
      }
    },
    {
      Header: '1st Payment',
      accessor: "date_ongoing",
      disableFilters: true,
      Cell: ({ row }) => {
        const { date_ongoing } = row.original
        return <span>{date_ongoing && moment(date_ongoing).format('ll')}</span>
      }
    },
    {
      accessor: `k_status`,
      Header: `K Status`,
      Filter: DefaultFilter,
      Cell: ({ row }) => {
        const { k_status } = row.original
        return <span>{k_status}</span>
      }
    },
    {
      accessor: `k_description`,
      Header: `K Description`,
      Filter: DefaultFilter,
      Cell: ({ row }) => {
        const { k_description } = row.original
        return <span>{k_description}</span>
      }
    },
    {
      Header: "Actions",
      Cell: ({ row }) => (
        <ProjectRowActions
          row={row}
          user={user}
          downloadContract={downloadContract}
          downloading={downloading}
          archive={archive}
          unarchive={unarchive}
          isArchiving={isArchiving}
          isUnarchiving={isUnarchiving}
          setReviewData={setReviewData}
          onUrlCopied={() => setUrlCopied(true)}
        />
      )
    },
  ]);
  const [open, setOpen] = useState(true)
  // const { data: projects, isLoading, refetch } = useQuery("projects", () => fetchProjects(null, null, null, user?.email === process.env.REACT_APP_FINANCE_ADMIN_EMAIL), {
  //   select: ({ projects }) => projects,
  //   enabled: false
  // })
  const { mutate: fetch, isLoading } = useMutation(params => fetchProjects(null, null, null, user?.email === process.env.REACT_APP_FINANCE_ADMIN_EMAIL, params), {
    onSuccess: ({ projects }) => {
      setProjects(projects.data || [])
      setTotal(projects.total)
      if (projects.pageCount !== pageCount) {
        setPageCount(projects.pageCount)
      }
    }
  })

  const { mutate: archive, isLoading: isArchiving } = useMutation(archiveProject, {
    onSuccess: () => {
      alert('Project archived successfully!')
      fetch({})
    },
    onError: (error) => {
      alert(error.data?.message || 'Oops, something went wrong!')
    }
  })

  const { mutate: unarchive, isLoading: isUnarchiving } = useMutation(unarchiveProject, {
    onSuccess: () => {
      alert('Project unarchived successfully!')
      fetch({})
    },
    onError: (error) => {
      alert(error.data?.message || 'Oops, something went wrong!')
    }
  })

  const { data: postOfferProjects } = useQuery('post-offer-projects', () => fetchPostOfferProjects(), {
    select: res => res.projects,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  })

  const handleRefetchData = useCallback(
    ({ pageIndex, pageSize, search }) => {
      fetch({
        pageIndex,
        pageSize,
        search,
      })
    },
    []
  )

  // useEffect(() => {
  //   if (user && user.email) {
  //     fetch({})
  //   }
  // }, [user])

  // if (userLoading) return <div>Loading....</div>

  return (
    <Container spacing={1} maxWidth='xl'>
      <Grid item lg={12}>
        {postOfferProjects === undefined ? null : <div>
          <span className="fw7">projects created from a post offer: {postOfferProjects?.length}</span>
        </div>}
        <div className="is-flex aic jcsb">
          <h2 className="mt-0 mb-5 mt-20">Projects (<i>{total}</i>)</h2>
          <div className="is-flex aic">
            {user?.email != process.env.REACT_APP_FINANCE_ADMIN_EMAIL && <a className="stllr-btn primary btn-md" href="/new-project">Create project</a>}
            {user?.email != process.env.REACT_APP_FINANCE_ADMIN_EMAIL && <a className="stllr-btn primary btn-lg ml-10" href="/new-project/ugc">Create UGC project</a>}
          </div>
        </div>

        <TableContainerServerSide
          name='projects'
          paging={true}
          columns={columns}
          hiddenColumns={user.email === process.env.REACT_APP_ADMIN_EMAIL ? [] : ["k_status", "k_description"]}
          data={projects}
          hasFilters={true}
          loading={isLoading}
          pageCount={pageCount}
          fetchData={handleRefetchData}
          dataFetcher={(params) => {
            const response = fetchProjects(null, null, null, user?.email === process.env.REACT_APP_FINANCE_ADMIN_EMAIL, params);
            
            // If export parameter is present, return raw response (CSV data)
            if (params.export) {
              return response;
            }
            
            // Otherwise, return the projects data for normal table display
            return response.then(res => res.projects);
          }}
          withExport={true}
        />
      </Grid>

      {projects.map(e => (
        <div>
          {e.pulsesNeeded > 0 && (e.pulsesNeeded > e.pulses.length) ?
            <Dialog open={open} onClose={() => setOpen(false)} maxWidth='sm' fullWidth={true}>
              <DialogTitle id="form-dialog-title">
                <div className='is-flex fdr aic jcsb'>
                  Projects that need to be pulsed
                  <IconButton onClick={() => setOpen(false)}>
                    {/* <CloseIcon /> */}
                    X
                  </IconButton>
                </div>
              </DialogTitle>
              <DialogContent>
                <div className='group'>
                  <div className="" style={{ fontSize: "14px", lineHeight: "17px" }}>
                    <div>
                      <div className="is-flex aic jcfs mb-15">
                        {/* {arr.push(e.id), //console.log(arr)} */}
                        <div>{e.name}</div>
                        <button onClick={() => window.location.href = `/pulses/${e.id}`} className="stllr-btn btn-sm primary bordered ml-10">Fill Pulse</button>
                      </div>
                    </div>
                  </div>
                </div>
              </DialogContent>
            </Dialog> : null}
        </div>
      ))}

      <RatingModal
        data={reviewData}
        isOpen={Boolean(reviewData)}
        onClose={() => setReviewData(null)}
      />

      <Snackbar
        open={urlCopied}
        autoHideDuration={3000}
        onClose={() => setUrlCopied(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        message="URL copied"
      />

    </Container>
  )
}

export default Projects